import { ThunkDispatch } from "redux-thunk";
import { AnyAction } from "redux";
import { AppState } from "../store";
import {
  ConversationAssignedEvent,
  ConversationTransferredEvent,
  ConversationUnlockedEvent,
  ConversationClosedEvent,
  ConversationReopenedEvent,
  ConversationUpdatedEvent,
  OperationFailedEvent,
  ConversationLockedEvent,
} from "./types";
import {
  addNotifications,
  getConversations,
  setReadConversation,
} from "../store/action-creators";
import { refreshConversationById } from "../store/action-creators";
import { http } from "../utils/http";

type AppDispatch = ThunkDispatch<AppState, unknown, AnyAction>;

const makeNotification = (message: string) => [
  {
    id: Date.now(),
    message,
    variant: "success" as const,
    dismissAfter: 3000,
  },
];

// ---------------------
// ConversationAssigned
// ---------------------
export function handleConversationAssigned(
  evt: ConversationAssignedEvent,
  dispatch: AppDispatch,
  getState: () => AppState
) {
  const state = getState();
  const myOperatorId = state.operator?.id;

  if (!myOperatorId) return;

  // interessa solo se la conversazione è assegnata a me
  if (evt.toOperatorId !== myOperatorId) return;

  dispatch(
    addNotifications(
      makeNotification("Ti è stata assegnata una nuova conversazione.")
    )
  );

  // ricarico la lista mantenendo l’eventuale query
  const currentQuery = state.conversations.query || undefined;
  dispatch(
    getConversations({
      page: 1,
      q: currentQuery,
    })
  );
}

// ---------------------
// ConversationTransferred
// ---------------------
export function handleConversationTransferred(
  evt: ConversationTransferredEvent,
  dispatch: AppDispatch,
  getState: () => AppState
) {
  const state = getState();
  const myOperatorId = state.operator?.id;

  if (!myOperatorId) return;

  // Questo controllo non serve perché dobbiamo aggiornare le conversazioni
  // anche a tutti gli utenti del team appartenenti all'operatore
  // che ha trasferito la conversazione ad un altro operatore
  // const isForMe = evt.toOperatorId === myOperatorId;
  // if (!isForMe) return;

  let message = "";
  if (evt.toOperatorId === myOperatorId) {
    message = "Ti è stata trasferita una conversazione.";
  }
  if (evt.fromOperatorId === myOperatorId) {
    message = "La conversazione è stata trasferita.";
  }

  if (message) {
    dispatch(addNotifications(makeNotification(message)));
  }

  const currentQuery = state.conversations.query || undefined;
  dispatch(
    getConversations({
      page: 1,
      q: currentQuery,
    })
  );
}

// ---------------------
// ConversationUnlocked
// ---------------------
export function handleConversationUnlocked(
  evt: ConversationUnlockedEvent,
  dispatch: AppDispatch,
  getState: () => AppState
) {
  const state = getState();

  // Fix: quando una conversazione viene sbloccata, la conversazione
  // può apparire anche a degli operatori che non ce l'hanno nella lista
  // "se uguale al mio": controllo se la conversazione è presente nella mia lista
  // const belongsToMe = state.conversations.items.some(
  //   (c) => c.id === evt.conversationId
  // );

  // if (!belongsToMe) return;

  const myOperatorId = state.operator?.id;
  if (!myOperatorId) return;

  let message = "";
  if (evt.byOperatorId === myOperatorId) {
    message = "La conversazione è stata sbloccata";
  }

  if (message) {
    dispatch(addNotifications(makeNotification(message)));
  }

  // const reasonLabel = evt.reasonId != null ? ` (reasonId: ${evt.reasonId})` : "";

  // aggiorno SOLO quella conversazione
  dispatch(refreshConversationById(evt.conversationId));
}

// ---------------------
// ConversationLocked
// ---------------------
export function handleConversationLocked(
  evt: ConversationLockedEvent,
  dispatch: AppDispatch,
  getState: () => AppState
) {
  const state = getState();

  const myOperatorId = state.operator?.id;
  if (!myOperatorId) return;

  let message = "";
  if (evt.byOperatorId === myOperatorId) {
    message = "La conversazione è stata bloccata";
  }

  if (message) {
    dispatch(addNotifications(makeNotification(message)));
  }

  // aggiorno SOLO quella conversazione
  dispatch(refreshConversationById(evt.conversationId));
}

// ---------------------
// ConversationClosed
// ---------------------
export function handleConversationClosed(
  evt: ConversationClosedEvent,
  dispatch: AppDispatch,
  getState: () => AppState
) {
  const state = getState();

  const belongsToMe = state.conversations.items.some(
    (c) => c.id === evt.conversationId
  );
  if (!belongsToMe) return;

  const reason = evt.reason ? ` Motivo: ${evt.reason}` : "";

  dispatch(
    addNotifications(
      makeNotification(`Una conversazione è stata chiusa.${reason}`)
    )
  );

  dispatch(refreshConversationById(evt.conversationId));
}

// ---------------------
// ConversationReopened
// ---------------------
export function handleConversationReopened(
  evt: ConversationReopenedEvent,
  dispatch: AppDispatch,
  getState: () => AppState
) {
  const state = getState();

  const belongsToMe = state.conversations.items.some(
    (c) => c.id === evt.conversationId
  );
  if (!belongsToMe) return;

  const note = evt.note ? ` Nota: ${evt.note}` : "";

  dispatch(
    addNotifications(
      makeNotification(`Una conversazione è stata riaperta.${note}`)
    )
  );

  dispatch(refreshConversationById(evt.conversationId));
}

// ---------------------
// ConversationUpdated
// ---------------------
export async function handleConversationUpdated(
  evt: ConversationUpdatedEvent,
  dispatch: AppDispatch,
  getState: () => AppState
) {
  const state = getState();

  const convo = state.conversations.items.find(
    (c) => c.id === evt.conversationId
  );
  if (!convo) return;

  const activeSid = state.sid;
  const isInThatChat = !!activeSid && activeSid === convo.twilioSid;

  if (!isInThatChat) {
    dispatch(
      addNotifications(
        makeNotification("Una conversazione è stata aggiornata.")
      )
    );
  }
  if (isInThatChat) {
    await http.put(`/api/Conversations/${evt.conversationId}/set-read`);
  }

  await dispatch(refreshConversationById(evt.conversationId));
}

// ---------------------
// OperationFailedEvent
// ---------------------
export async function handleOperationFailed(
  evt: OperationFailedEvent,
  dispatch: AppDispatch,
  getState: () => AppState
) {
  const state = getState();

  const operatorIdFromEvent = evt.OperatorId ?? evt.operatorId ?? "";
  const message = evt.Message ?? evt.message ?? "Operazione fallita.";

  const myOperatorId = state.operator?.id;
  if (!myOperatorId || operatorIdFromEvent !== myOperatorId) return;

  dispatch(
    addNotifications([
      {
        id: Date.now(),
        message,
        variant: "error",
        dismissAfter: 3000,
      },
    ])
  );
}
